// valuePass/admin/src/components/layout/Sidebar.tsx
//
// Main navigation sidebar for the ValuePass admin dashboard.
//
// ── Responsibilities ──────────────────────────────────────────────────────────
//
//   • Renders the ValuePass brand logo and wordmark
//   • Shows the active campaign selector (dropdown)
//   • Renders navigation links with active-state highlighting
//   • Collapses to an icon-only rail on tablet and mobile (toggled by AppShell)
//   • Shows the signed-in user's avatar and name at the bottom
//
// ── Active campaign ───────────────────────────────────────────────────────────
//
//   The "active campaign" is the campaign currently being administered. It is
//   stored in a React context (CampaignContext) so that every page and component
//   in the app can read it without prop-drilling. The sidebar renders a dropdown
//   that lets the user switch between campaigns.
//
//   When collapsed, only the campaign's initials are shown.
//
// ── Routing ───────────────────────────────────────────────────────────────────
//
//   Uses React Router v6 NavLink for automatic active-state detection.
//   Each NavLink uses the `end` prop where appropriate to prevent false
//   positives on parent routes.
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";

// ── Icon components ───────────────────────────────────────────────────────────
//
// Inline SVG icons to avoid a heavy icon-library dependency.
// Each icon is a 20×20 viewBox SVG wrapped in a span.

const Icon: React.FC<{
  path:      string;
  className?: string;
  title?:    string;
}> = ({ path, className = "w-5 h-5", title }) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden={!title}
    role={title ? "img" : undefined}
  >
    {title && <title>{title}</title>}
    <path d={path} />
  </svg>
);

// Icon paths (Material Design / Heroicons style, 20×20)
const ICONS = {
  dashboard:
    "M2 3a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1V3zm9 0a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1V3zM2 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1v-4zm9 1a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z",
  vouchers:
    "M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM2 9h16v6a2 2 0 01-2 2H4a2 2 0 01-2-2V9zm4 2a1 1 0 000 2h8a1 1 0 100-2H6z",
  beneficiaries:
    "M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z",
  campaigns:
    "M2 4a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H3a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm9 0a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z",
  analytics:
    "M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-4a1 1 0 011-1h2a1 1 0 011 1v13a1 1 0 01-1 1h-2a1 1 0 01-1-1V3z",
  merchants:
    "M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z",
  settings:
    "M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z",
  chevronDown:
    "M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z",
  logout:
    "M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z",
  menu:
    "M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z",
  close:
    "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z",
  check:
    "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
} as const;

// ── Navigation items ──────────────────────────────────────────────────────────

interface NavItem {
  label:    string;
  to:       string;
  iconKey:  keyof typeof ICONS;
  end?:     boolean;
  badge?:   string;
}

const PRIMARY_NAV: NavItem[] = [
  { label: "Dashboard",     to: "/",             iconKey: "dashboard",     end: true },
  { label: "Campaigns",     to: "/campaigns",    iconKey: "campaigns" },
  { label: "Vouchers",      to: "/vouchers",     iconKey: "vouchers" },
  { label: "Beneficiaries", to: "/beneficiaries",iconKey: "beneficiaries" },
  { label: "Analytics",     to: "/analytics",    iconKey: "analytics" },
  { label: "Merchants",     to: "/merchants",    iconKey: "merchants" },
];

const SECONDARY_NAV: NavItem[] = [
  { label: "Settings", to: "/settings", iconKey: "settings" },
];

// ── Campaign dropdown item ────────────────────────────────────────────────────

interface CampaignOption {
  campaignId: string;
  name:       string;
  status:     string;
}

// Status dot colour
function statusDotClass(status: string): string {
  switch (status.toUpperCase()) {
    case "ACTIVE":  return "bg-green-500";
    case "PAUSED":  return "bg-amber-400";
    case "DRAFT":   return "bg-blue-400";
    default:        return "bg-gray-400";
  }
}

// ── NavLink item ──────────────────────────────────────────────────────────────

const SidebarNavItem: React.FC<{
  item:      NavItem;
  collapsed: boolean;
}> = ({ item, collapsed }) => (
  <NavLink
    to={item.to}
    end={item.end}
    className={({ isActive }) =>
      [
        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium",
        "transition-colors duration-100 group relative",
        isActive
          ? "bg-brand-green text-white shadow-sm"
          : "text-gray-300 hover:bg-white/10 hover:text-white",
        collapsed ? "justify-center" : "",
      ]
        .filter(Boolean)
        .join(" ")
    }
    title={collapsed ? item.label : undefined}
  >
    {({ isActive }) => (
      <>
        <Icon
          path={ICONS[item.iconKey]}
          className={`w-5 h-5 flex-shrink-0 transition-colors ${
            isActive ? "text-white" : "text-gray-400 group-hover:text-white"
          }`}
        />
        {!collapsed && (
          <span className="truncate leading-none">{item.label}</span>
        )}
        {!collapsed && item.badge && (
          <span className="ml-auto text-xs bg-brand-gold text-brand-green font-bold px-1.5 py-0.5 rounded-full leading-none">
            {item.badge}
          </span>
        )}
        {/* Tooltip for collapsed mode */}
        {collapsed && (
          <span className="absolute left-full ml-2 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
            {item.label}
          </span>
        )}
      </>
    )}
  </NavLink>
);

// ── Campaign selector dropdown ────────────────────────────────────────────────

const CampaignSelector: React.FC<{
  campaigns:   CampaignOption[];
  activeCampaignId: string | null;
  onSelect:    (id: string) => void;
  collapsed:   boolean;
}> = ({ campaigns, activeCampaignId, onSelect, collapsed }) => {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  const active = campaigns.find((c) => c.campaignId === activeCampaignId);
  const activeName = activeCampaignId === null ? "All campaigns" : (active?.name ?? "Select campaign");

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (campaigns.length === 0) return null;

  // Collapsed: just show initials as a button
  if (collapsed) {
    return (
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-8 h-8 rounded-lg bg-brand-gold/20 border border-brand-gold/30 text-brand-gold text-xs font-bold flex items-center justify-center hover:bg-brand-gold/30 transition-colors"
        title={activeName}
      >
        {activeName.slice(0, 2).toUpperCase()}
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 transition-colors text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {/* Campaign initials badge */}
        <span className="flex-shrink-0 w-6 h-6 rounded bg-brand-gold/30 border border-brand-gold/40 text-brand-gold text-xs font-bold flex items-center justify-center">
          {(active?.name ?? "?").slice(0, 2).toUpperCase()}
        </span>
        <span className="flex-1 truncate text-xs font-semibold text-white">
          {activeName}
        </span>
        <Icon
          path={ICONS.chevronDown}
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50 max-h-60 overflow-y-auto"
        >
          <li key="__all__">
            <button
              role="option"
              aria-selected={activeCampaignId === null}
              onClick={() => {
                onSelect("");
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-400" />
              <span
                className={`flex-1 truncate ${
                  activeCampaignId === null
                    ? "text-white font-semibold"
                    : "text-gray-300"
                }`}
              >
                All campaigns
              </span>
              {activeCampaignId === null && (
                <Icon
                  path={ICONS.check}
                  className="w-3.5 h-3.5 text-brand-gold flex-shrink-0"
                />
              )}
            </button>
          </li>
          {campaigns.map((c) => {
            const isSelected = c.campaignId === activeCampaignId;
            return (
              <li key={c.campaignId}>
                <button
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onSelect(c.campaignId);
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotClass(c.status)}`}
                  />
                  <span
                    className={`flex-1 truncate ${
                      isSelected ? "text-white font-semibold" : "text-gray-300"
                    }`}
                  >
                    {c.name}
                  </span>
                  {isSelected && (
                    <Icon
                      path={ICONS.check}
                      className="w-3.5 h-3.5 text-brand-gold flex-shrink-0"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ── User avatar + sign-out ────────────────────────────────────────────────────

const UserFooter: React.FC<{
  displayName: string | null;
  email:       string | null;
  photoURL:    string | null;
  collapsed:   boolean;
}> = ({ displayName, email, photoURL, collapsed }) => {
  const navigate   = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleSignOut = async () => {
    setBusy(true);
    try {
      await signOut(getAuth());
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("[Sidebar] signOut failed:", err);
      setBusy(false);
    }
  };

  const initials = displayName
    ? displayName
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : email?.[0]?.toUpperCase() ?? "?";

  return (
    <div className="flex items-center gap-2 px-2 py-3 border-t border-white/10">
      {/* Avatar */}
      <div className="flex-shrink-0 relative">
        {photoURL ? (
          <img
            src={photoURL}
            alt={displayName ?? "User avatar"}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <span className="w-8 h-8 rounded-full bg-brand-gold/20 border border-brand-gold/30 text-brand-gold text-xs font-bold flex items-center justify-center">
            {initials}
          </span>
        )}
        {/* Online indicator */}
        <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-green-400 border border-gray-800" />
      </div>

      {!collapsed && (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">
              {displayName ?? "Admin"}
            </p>
            <p className="text-xs text-gray-400 truncate">{email ?? ""}</p>
          </div>

          <button
            onClick={handleSignOut}
            disabled={busy}
            className="flex-shrink-0 p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
            title="Sign out"
            aria-label="Sign out"
          >
            <Icon path={ICONS.logout} className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────

export interface SidebarProps {
  /** When true the sidebar shows only icons (narrow rail). */
  collapsed:        boolean;
  /** List of campaigns to show in the dropdown. */
  campaigns:        CampaignOption[];
  /** The currently selected campaign ID. */
  activeCampaignId: string | null;
  /** Callback when the user selects a different campaign. */
  onCampaignChange: (id: string) => void;
  /** Currently signed-in user (from Firebase Auth). */
  user: {
    displayName: string | null;
    email:       string | null;
    photoURL:    string | null;
  } | null;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  campaigns,
  activeCampaignId,
  onCampaignChange,
  user,
}) => {
  const width = collapsed ? "w-16" : "w-56";

  return (
    <aside
      className={`
        ${width}
        flex-shrink-0
        h-full
        bg-gray-800
        flex flex-col
        transition-[width] duration-200 ease-in-out
        overflow-hidden
      `}
      aria-label="Main navigation"
    >
      {/* ── Logo ──────────────────────────────────────────────────────── */}
      <div
        className={`flex items-center gap-3 px-3 py-4 border-b border-white/10 ${
          collapsed ? "justify-center" : ""
        }`}
      >
        {/* Brand mark */}
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-green border-2 border-brand-gold flex items-center justify-center shadow-brand">
          <span className="font-bold text-brand-gold text-sm leading-none select-none">
            vp
          </span>
        </div>
        {!collapsed && (
          <div className="leading-tight min-w-0">
            <div className="text-sm font-bold text-white tracking-tight">
              <span className="text-brand-gold">Value</span>Pass
            </div>
            <div className="text-xs text-gray-400">Admin Dashboard</div>
          </div>
        )}
      </div>

      {/* ── Campaign selector ─────────────────────────────────────────── */}
      {campaigns.length > 0 && (
        <div className={`px-2 pt-3 pb-2 ${collapsed ? "flex justify-center" : ""}`}>
          {!collapsed && (
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold px-1 mb-1.5">
              Campaign
            </p>
          )}
          <CampaignSelector
            campaigns={campaigns}
            activeCampaignId={activeCampaignId}
            onSelect={onCampaignChange}
            collapsed={collapsed}
          />
        </div>
      )}

      {/* ── Divider ───────────────────────────────────────────────────── */}
      <div className="mx-3 my-1 border-t border-white/5" />

      {/* ── Primary navigation ────────────────────────────────────────── */}
      <nav
        className="flex-1 overflow-y-auto px-2 py-1 flex flex-col gap-0.5"
        aria-label="Primary navigation"
      >
        {!collapsed && (
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold px-1 mb-1 mt-1">
            Navigation
          </p>
        )}
        {PRIMARY_NAV.map((item) => (
          <SidebarNavItem key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* ── Secondary navigation (settings) ──────────────────────────── */}
      <nav className="px-2 pb-1 flex flex-col gap-0.5">
        {SECONDARY_NAV.map((item) => (
          <SidebarNavItem key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* ── User footer ───────────────────────────────────────────────── */}
      {user && (
        <div className="px-2 pb-2">
          <UserFooter
            displayName={user.displayName}
            email={user.email}
            photoURL={user.photoURL}
            collapsed={collapsed}
          />
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
